import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsArrowLeft, BsArrowRight, BsStarFill, BsStar } from 'react-icons/bs';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper';
import { urlFor, client } from '../../client';
import { AppWrap } from '../../wrapper';

import 'swiper/css';
import './Testimonials.scss';

const fadeUp = {
	hidden: { y: 40, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const slideVariants = {
	enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
	center: { opacity: 1, x: 0 },
	exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60 }),
};

const Testimonials = () => {
	const [testimonials, setTestimonials] = useState([]);
	const [brands, setBrands] = useState([]);
	const [index, setIndex] = useState(0);
	const [direction, setDirection] = useState(1);

	useEffect(() => {
		let mounted = true;
		client.fetch('*[_type == "testimonials"]').then((data) => {
			if (mounted) setTestimonials(data || []);
		});
		client.fetch('*[_type == "brands"]').then((data) => {
			if (mounted) setBrands(data || []);
		});
		return () => {
			mounted = false;
		};
	}, []);

	const paginate = (dir) => {
		if (testimonials.length === 0) return;
		setDirection(dir);
		setIndex((prev) => (prev + dir + testimonials.length) % testimonials.length);
	};

	const goTo = (i) => {
		setDirection(i > index ? 1 : -1);
		setIndex(i);
	};

	const active = testimonials[index];
	const total = testimonials.length;
	const rating = active
		? Math.max(0, Math.min(5, Math.round(active.rating || 5)))
		: 5;

	return (
		<div className='testimonials-content'>
			<motion.div
				className='testimonials-head'
				variants={fadeUp}
				initial='hidden'
				whileInView='visible'
				viewport={{ once: true, amount: 0.4 }}
			>
				<p className='eyebrow'>Kind Words</p>
				<h2 className='testimonials-heading'>
					Client <span className='text-gradient'>testimonials</span>
				</h2>
				<p className='testimonials-intro'>
					Feedback from the people I've partnered with — the clearest signal of what it's
					like to build together.
				</p>
			</motion.div>

			{active && (
				<motion.div
					className='testimonial-slider'
					variants={fadeUp}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.25 }}
				>
					<AnimatePresence mode='wait' custom={direction} initial={false}>
						<motion.blockquote
							className='review-card'
							key={index}
							custom={direction}
							variants={slideVariants}
							initial='enter'
							animate='center'
							exit='exit'
							transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
							drag='x'
							dragConstraints={{ left: 0, right: 0 }}
							dragElastic={0.16}
							onDragEnd={(e, info) => {
								if (info.offset.x < -80) paginate(1);
								else if (info.offset.x > 80) paginate(-1);
							}}
						>
							<div
								className='review-stars'
								role='img'
								aria-label={`Rated ${rating} out of 5`}
							>
								{Array.from({ length: 5 }).map((_, i) =>
									i < rating ? (
										<BsStarFill key={i} aria-hidden='true' />
									) : (
										<BsStar key={i} aria-hidden='true' />
									)
								)}
							</div>

							<p className='review-text'>{active.feedback}</p>

							<footer className='review-author'>
								<span className='review-avatar'>
									<img src={urlFor(active.imageurl)} alt={active.name} />
								</span>
								<span className='review-name'>{active.name}</span>
								<span className='review-company'>{active.company}</span>
							</footer>
						</motion.blockquote>
					</AnimatePresence>

					{total > 1 && (
						<div className='review-controls'>
							<button
								type='button'
								className='slider-btn'
								onClick={() => paginate(-1)}
								aria-label='Previous testimonial'
							>
								<BsArrowLeft />
							</button>

							<div className='review-dots' role='tablist' aria-label='Select testimonial'>
								{testimonials.map((item, i) => (
									<button
										type='button'
										key={item._id || i}
										className={`dot ${i === index ? 'active' : ''}`}
										onClick={() => goTo(i)}
										aria-label={`Show testimonial ${i + 1}`}
										aria-current={i === index}
									/>
								))}
							</div>

							<button
								type='button'
								className='slider-btn'
								onClick={() => paginate(1)}
								aria-label='Next testimonial'
							>
								<BsArrowRight />
							</button>
						</div>
					)}
				</motion.div>
			)}

			{brands.length > 0 && (
				<motion.div
					className='brands-block'
					variants={fadeUp}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.3 }}
				>
					<h3 className='testimonials-subhead'>
						Trusted by <span>prominent clients</span>
					</h3>

					<div className='brands-marquee'>
						<Swiper
							modules={[Autoplay]}
							slidesPerView={2}
							spaceBetween={24}
							loop={brands.length > 4}
							speed={4000}
							grabCursor
							autoplay={{ delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true }}
							breakpoints={{
								600: { slidesPerView: 3 },
								900: { slidesPerView: 4 },
								1200: { slidesPerView: 5 },
							}}
						>
							{brands.map((brand, i) => (
								<SwiperSlide key={brand._id || i}>
									<div className='brand-logo'>
										<img src={urlFor(brand.imgUrl)} alt={brand.name} loading='lazy' />
									</div>
								</SwiperSlide>
							))}
						</Swiper>
					</div>
				</motion.div>
			)}
		</div>
	);
};

export default AppWrap(Testimonials, 'testimonials');
